import { FeBundleRepository } from "containers/BundleRepositories/types";
import { ActionData } from "containers/Resources/components/CommonActions/Action";
import { actions as bundlesActions } from "./Bundles.reducer";
import { importedTabKey, updateTabKey } from "./constants";
import { BundleResponseData, BundleTableData } from "./interfaces";

/*
import => version && !installed_version
update => version && installed_version && version !== installed_version
re-install => version && installed_version && version === installed_version && is_dirty 
uninstall => version && installed_version
release => installed_version && installed_version !== version && repository.has_write_key;
*/

export const fetchBundlesData = (dispatch, bundleRepositoryData) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: bundlesActions.START_FETCHING_BUNDLES,
            promise: { resolve, reject },
            bundleRepositoryData,
        });
    }).catch((error) => error);
};

/**
 * Fetches bundleRepositoryId and updates bundleResponse in redux store
 */
export const fetchSingleBundleRepository = ({
    dispatch,
    bundleRepositoryId,
    bundleRepositoryName,
}) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: bundlesActions.START_FETCHING_BUNDLE_REPOSITORY,
            promise: { resolve, reject },
            bundleRepositoryId,
            bundleRepositoryName,
        });
    }).catch((error) => error);
};

export const canInstall = (bundle) => bundle.version && !bundle.installed_version;
const checkInstalled = (bundle) => bundle.installed_version && bundle.version;
const isUpdateNeeded = (bundle) => {
    const { version, installed_version } = bundle;
    return version && installed_version && version !== installed_version;
};

const getMergedBundleList = (
    bundleResponse: BundleResponseData,
    bundleRepositoriesList: FeBundleRepository[]
) => {
    let result: BundleTableData[] = [];
    for (const value of Object.values(bundleResponse)) {
        const bundleData = value.bundle_list?.map((bundle) => ({
            ...bundle,
            repository_name: value.name,
            repository_id: value.id,
            repository_has_write_key:
                bundleRepositoriesList.find((repo) => repo.id === value.id)?.has_write_key ?? false,
            resource_type: "bundle",
        }));
        result.push(...bundleData);
    }

    return result;
};

export const getBundleTableData = (
    bundleList: BundleResponseData,
    bundleRepositoriesList: FeBundleRepository[],
    activeTab: string
) => {
    const mergedData = getMergedBundleList(bundleList, bundleRepositoriesList);

    switch (activeTab) {
        case importedTabKey: {
            return mergedData.filter(checkInstalled);
        }
        case updateTabKey: {
            return mergedData.filter(isUpdateNeeded);
        }

        default:
            return mergedData;
    }
};

export const getButtonClassName = (isButtonDisabled, isInlineButton) =>
    isButtonDisabled && isInlineButton ? "hide-inline-button" : "";

const isInstanceOfActionData = (object: any): object is ActionData => {
    return "remoteBundleVersion" in object;
};

const canApplyAction = ({ data, canApplyBulkAction, canApplySingleAction }) => {
    if (isInstanceOfActionData(data)) {
        return canApplySingleAction;
    } else {
        return canApplyBulkAction;
    }
};

export const canUpdate = (data) => {
    const canApplyBulkAction = isUpdateNeeded(data.resourceData);
    const canApplySingleAction =
        data.resourceData.version &&
        data.remoteBundleVersion &&
        data.resourceData.version !== data.remoteBundleVersion;
    return canApplyAction({ data, canApplyBulkAction, canApplySingleAction });
};
export const canReInstall = (data) => {
    const canApplyBulkAction =
        data.resourceData.installed_version &&
        data.resourceData.installed_version === data.resourceData.version &&
        data.resourceData.is_dirty;
    const canApplySingleAction =
        data.resourceData.version &&
        data.resourceData.version === data.remoteBundleVersion &&
        data.resourceData.is_dirty;
    return canApplyAction({ data, canApplyBulkAction, canApplySingleAction });
};
export const canRelease = (data) => {
    const canApplyBulkAction =
        data.resourceData.installed_version &&
        data.resourceData.installed_version !== data.resourceData.version &&
        data.resourceData.repository_has_write_key;
    const canApplySingleAction =
        data?.resourceData?.version &&
        data?.resourceData?.version !== data.remoteBundleVersion &&
        data?.resourceData?.bundle_repository_id_bundle_repository?.name &&
        data?.resourceData?.bundle_repository_id_bundle_repository?.has_write_key;

    return canApplyAction({ data, canApplyBulkAction, canApplySingleAction });
};
export const canUninstall = (data) => {
    const canApplyBulkAction = checkInstalled(data.resourceData);
    const canApplySingleAction = data.remoteBundleVersion && data.resourceData.version;

    return canApplyAction({ data, canApplyBulkAction, canApplySingleAction });
};
// Purge (remove from the remote repository) is possible when the bundle is present
// in the repo (has a released/remote version) and we hold the repository write key.
export const canPurge = (data) => {
    const canApplyBulkAction =
        data.resourceData.version && data.resourceData.repository_has_write_key;
    const canApplySingleAction =
        data.remoteBundleVersion &&
        data?.resourceData?.bundle_repository_id_bundle_repository?.name &&
        data?.resourceData?.bundle_repository_id_bundle_repository?.has_write_key;

    return canApplyAction({ data, canApplyBulkAction, canApplySingleAction });
};
